/**
 * D5: Learner Preferences (sound, reducedMotion, dailyGoal)
 * Source: src/components/settings/SettingsPage.tsx
 */

import { isBackendEnabled } from '../helpers';

// Per domein — dit domein heeft nog geen backend. Zie helpers.ts.
const USE_BACKEND = isBackendEnabled('learnerPrefs');
const LEARNER_PREFS_KEY = 'meester-djay-learner-prefs';
export interface LearnerPreferences {
  sound: boolean;
  haptic: boolean;
  reducedMotion: boolean;
  dailyGoal: 5 | 10 | 20;
  weeklyGoal: 3 | 5 | 7;
  /**
   * Sound mix (App-Feel 2027 W2). All values are 0..1 gain multipliers,
   * default 1. `masterVolume` scales every category; the per-category
   * fields scale a single intent group on top of master:
   *   - `voiceVolume`       → Djay's stem (mascot.speak)
   *   - `celebrationVolume` → Vieringen (reward.* + answer.correct)
   *   - `uiVolume`          → Klikgeluiden / counters / neutral cues
   * The actual gain conversion happens in soundManager (single boundary).
   */
  masterVolume: number;
  voiceVolume: number;
  celebrationVolume: number;
  uiVolume: number;
  avatarUrl?: string;
  hideCreateAssessmentTip?: boolean;
  hideCreatePracticeTip?: boolean;
  /**
   * Subject (module id) the learner picked during onboarding as their
   * first focus area. Consumed once by the home page on the next visit
   * so FloatingDjay can point at the chosen subject; cleared after use.
   * Format: a ModuleId like 'REK' / 'GET' / 'MET' / 'SPR' / 'TAA' / 'TIJD'.
   */
  firstFocusSubject?: string;
  pendingAppTour?: boolean;
  tourCompleted?: boolean;
}
const DEFAULT_LEARNER_PREFS: LearnerPreferences = {
  sound: true,
  haptic: true,
  reducedMotion: false,
  dailyGoal: 10,
  weeklyGoal: 3,
  masterVolume: 1,
  voiceVolume: 1,
  celebrationVolume: 1,
  uiVolume: 1,
  hideCreateAssessmentTip: false,
  hideCreatePracticeTip: false,
  pendingAppTour: false,
  tourCompleted: false
};

/** Clamp a persisted volume to 0..1, falling back to 1 for invalid values. */
function clampVolume(raw: unknown): number {
  return typeof raw === 'number' && Number.isFinite(raw) ? Math.min(1, Math.max(0, raw)) : 1;
}
export async function getLearnerPreferences(): Promise<LearnerPreferences> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented');
  }
  try {
    const raw = localStorage.getItem(LEARNER_PREFS_KEY);
    if (!raw) return {
      ...DEFAULT_LEARNER_PREFS
    };
    const parsed = JSON.parse(raw);
    return {
      sound: parsed.sound ?? true,
      haptic: parsed.haptic ?? true,
      reducedMotion: parsed.reducedMotion ?? false,
      dailyGoal: [5, 10, 20].includes(parsed.dailyGoal) ? parsed.dailyGoal : 10,
      weeklyGoal: [3, 5, 7].includes(parsed.weeklyGoal) ? parsed.weeklyGoal : 3,
      masterVolume: clampVolume(parsed.masterVolume),
      voiceVolume: clampVolume(parsed.voiceVolume),
      celebrationVolume: clampVolume(parsed.celebrationVolume),
      uiVolume: clampVolume(parsed.uiVolume),
      avatarUrl: parsed.avatarUrl ?? undefined,
      hideCreateAssessmentTip: parsed.hideCreateAssessmentTip ?? false,
      hideCreatePracticeTip: parsed.hideCreatePracticeTip ?? false,
      firstFocusSubject: typeof parsed.firstFocusSubject === 'string' ? parsed.firstFocusSubject : undefined,
      pendingAppTour: parsed.pendingAppTour ?? false,
      tourCompleted: parsed.tourCompleted ?? false
    };
  } catch {
    return {
      ...DEFAULT_LEARNER_PREFS
    };
  }
}
export async function saveLearnerPreferences(prefs: LearnerPreferences): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented');
  }
  try {
    localStorage.setItem(LEARNER_PREFS_KEY, JSON.stringify(prefs));
  } catch {

    // silent
  }
}